import React, { useState } from 'react';
import Header from './Header';
import Categories from './Categories';
import ContactForm from './ContactForm';
import BulkOrderForm from './BulkOrderForm';
import Footer from './Footer';
import WhatsAppFloat from './WhatsAppFloat';
import Cart from './Cart';
import VideoCarousel from './VideoCarousel';
import MarqueeBar from './MarqueeBar';
import Seo from '../common/Seo';
import { uiImages } from '../../assets/images';
import './UserView.css';

const MOOD_CARDS = [
    {
        title: 'School Uniforms',
        description: 'Complete uniform set dresses for schools — shirt, tie, and skirt or trouser sets in durable handwoven fabrics, with consistent sizing and bulk order support.',
        image: uiImages.schoolUniforms,
    },
    {
        title: 'Custom Design',
        description: 'Bring your own design to life. Our master weavers work with you to create unique patterns and fabrics made just for you.',
        image: uiImages.weavingCloseup,
    },
    {
        title: 'Handloom Sarees',
        description: 'Exquisite sarees woven on traditional looms — each piece a celebration of heritage craftsmanship and timeless elegance.',
        image: uiImages.silkFabric,
    },
    {
        title: 'Bulk Orders',
        description: 'Planning a large order? We welcome bulk enquiries for uniforms, events, and custom textile requirements at competitive rates.',
        image: uiImages.textileRoll,
    },
];

const REVIEWS = [
    {
        title: 'Beautiful craftsmanship',
        text: 'The saree I ordered exceeded my expectations. The weave is intricate, the fabric feels premium, and you can tell it was made with real care.',
        author: 'Priya R',
    },
    {
        title: 'Perfect school uniforms',
        text: 'We ordered uniforms for our school in bulk. The quality was consistent, delivery was on time, and the team was very responsive throughout.',
        author: 'Karthik M',
    },
    {
        title: 'Custom design done right',
        text: 'I shared my own design and they brought it to life beautifully. The fabric quality and attention to detail were outstanding.',
        author: 'Lakshmi S',
    },
    {
        title: 'Tradition you can feel',
        text: 'Every piece tells a story of handloom heritage. Proud to support local artisans through Namma Nesavu Nool.',
        author: 'Anitha V',
    },
];

const UserView: React.FC = () => {
    const [showContactForm, setShowContactForm] = useState(false);
    const [showBulkOrderForm, setShowBulkOrderForm] = useState(false);
    const [showCart, setShowCart] = useState(false);
    const [showCartCheckout, setShowCartCheckout] = useState(false);

    const handleEnquireClick = () => {
        setShowContactForm(true);
    };

    const handleBulkOrderClick = () => {
        setShowBulkOrderForm(true);
    };

    const scrollToCategories = () => {
        const element = document.getElementById('categories');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    React.useEffect(() => {
        const handleOpenCart = (e: CustomEvent) => {
            setShowCart(true);
            if ((e as any).detail?.checkout) {
                setShowCartCheckout(true);
            }
        };
        window.addEventListener('openCart', handleOpenCart as EventListener);
        return () => {
            window.removeEventListener('openCart', handleOpenCart as EventListener);
        };
    }, []);

    return (
        <div className="user-view">
            <Seo />
            <MarqueeBar
                variant="top"
                items={['handwoven with care', 'tradition meets trend', 'effortlessly elegant']}
            />

            <Header onCartClick={() => setShowCart(true)} />

            <main className="main-content">
                <section
                    className="hero"
                    aria-label="Welcome to Namma Nesavu Nool"
                    style={{
                        backgroundImage: `linear-gradient(135deg, rgba(47, 25, 72, 0.78) 0%, rgba(254, 67, 26, 0.5) 100%), url(${uiImages.heroBg})`,
                    }}
                >
                    <div className="hero__overlay" />
                    <div className="container hero__inner">
                        <p className="hero__eyebrow">Namma Nesavu Nool</p>
                        <h1 className="hero__title">
                            Handwoven Textiles<br />&amp; Traditional Fabrics
                        </h1>
                        <p className="hero__subtitle">
                            Crafted for those who cherish heritage weaving, timeless patterns,
                            and fabrics woven with heart — from sarees to school uniforms.
                        </p>
                        <button className="pill-btn pill-btn--light" onClick={scrollToCategories}>
                            Shop Now
                        </button>
                    </div>
                </section>

                <MarqueeBar
                    variant="features"
                    items={['handloom woven', 'premium quality', 'custom design', 'bulk orders welcome']}
                />

                <section className="visual-showcase" aria-label="Textile craftsmanship highlights">
                    <div className="visual-showcase__grid">
                        <figure className="visual-showcase__item visual-showcase__item--large">
                            <img src={uiImages.lifestyleBg} alt="Artisan handloom weaving workshop" loading="lazy" />
                        </figure>
                        <figure className="visual-showcase__item">
                            <img src={uiImages.threadSpools} alt="Colorful thread spools for weaving" loading="lazy" />
                        </figure>
                        <figure className="visual-showcase__item">
                            <img src={uiImages.fabricTexture} alt="Rich handwoven fabric texture" loading="lazy" />
                        </figure>
                    </div>
                </section>

                <Categories />

                <section id="about" className="about">
                    <div className="container about__grid">
                        <div className="about__media">
                            <img
                                src={uiImages.weavingCloseup}
                                alt="Close-up of traditional handloom weaving"
                                loading="lazy"
                            />
                            <img
                                className="about__media-accent"
                                src={uiImages.fabricColorful}
                                alt="Colorful woven textile patterns"
                                loading="lazy"
                            />
                        </div>
                        <div className="about__content">
                            <h2 className="display-heading">
                                Clean, thoughtful, feel-good textiles that honor joyful traditions
                            </h2>
                            <p className="about__lead">
                                Namma Nesavu Nool isn&apos;t just another textile brand. It&apos;s a celebration
                                of how we dress, gather, and carry forward the art of handloom weaving.
                            </p>
                            <button className="pill-btn pill-btn--outline" onClick={handleEnquireClick}>
                                About Us
                            </button>
                        </div>
                    </div>
                </section>

                <section
                    className="joy-life"
                    style={{
                        backgroundImage: `linear-gradient(135deg, rgba(47, 25, 72, 0.55) 0%, rgba(254, 67, 26, 0.35) 100%), url(${uiImages.lifestyleBg})`,
                    }}
                >
                    <div className="joy-life__overlay" />
                    <div className="container joy-life__inner">
                        <h2 className="joy-life__title">
                            Life is woven.<br />Beauty should be too.
                        </h2>
                        <p className="joy-life__text">
                            Namma Nesavu Nool is crafted to turn everyday fabrics into
                            meaningful pieces — joyful harmony in every thread.
                        </p>
                    </div>
                </section>

                <section className="mood-section">
                    <div className="container">
                        <div className="mood-grid">
                            {MOOD_CARDS.map((card) => (
                                <article key={card.title} className="mood-card">
                                    <div className="mood-card__image">
                                        <img src={card.image} alt={card.title} loading="lazy" />
                                    </div>
                                    <div className="mood-card__body">
                                        <h3>{card.title}</h3>
                                        <p>{card.description}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="production-process">
                    <div className="container">
                        <h2 className="section-heading">
                            <span>Where craft</span>
                            <span>meets tradition</span>
                        </h2>
                        <p className="section-subheading">
                            We provide handwoven alternatives that match your need for quality,
                            heritage, and fabrics made with purpose.
                        </p>
                        <div className="process-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <h3>Yarn Selection</h3>
                                <p>We carefully choose premium natural fibers — cotton, silk, and traditional threads.</p>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <h3>Warping &amp; Setting</h3>
                                <p>Our weavers meticulously set up the loom, preparing the foundation for weaving.</p>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <h3>Handloom Weaving</h3>
                                <p>Master weavers interlace threads on traditional handlooms, creating intricate patterns.</p>
                            </div>
                            <div className="step">
                                <div className="step-number">4</div>
                                <h3>Finishing &amp; Quality</h3>
                                <p>Each piece is carefully finished and checked to showcase traditional craftsmanship.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="reviews-section">
                    <div className="container">
                        <h2 className="section-heading section-heading--center">
                            <span>Don&apos;t take our</span>
                            <span>word for it</span>
                        </h2>
                        <div className="reviews-grid">
                            {REVIEWS.map((review) => (
                                <article key={review.author} className="review-card">
                                    <h3>{review.title}</h3>
                                    <p>{review.text}</p>
                                    <span className="review-author">{review.author}</span>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bundle-section">
                    <div
                        className="bundle-section__bg"
                        aria-hidden="true"
                        style={{
                            backgroundImage: `linear-gradient(135deg, rgba(254, 67, 26, 0.92) 0%, rgba(47, 25, 72, 0.85) 100%), url(${uiImages.threadSpools})`,
                        }}
                    />
                    <div className="container bundle-section__inner">
                        <h2 className="bundle-section__title">Bulk Orders &amp; Save</h2>
                        <p className="bundle-section__text">
                            Planning uniforms for a school or a large custom order?
                            Curate your collection with us — a little something for every need.
                        </p>
                        <div className="bundle-tiers">
                            <div className="bundle-tier">
                                <span className="bundle-tier__label">Custom design</span>
                                <span className="bundle-tier__value">Your pattern</span>
                            </div>
                            <div className="bundle-tier">
                                <span className="bundle-tier__label">School uniforms</span>
                                <span className="bundle-tier__value">Bulk pricing</span>
                            </div>
                            <div className="bundle-tier">
                                <span className="bundle-tier__label">Large orders</span>
                                <span className="bundle-tier__value">Best rates</span>
                            </div>
                        </div>
                        <button className="pill-btn pill-btn--light" onClick={handleBulkOrderClick}>
                            Enquire for Bulk Orders
                        </button>
                    </div>
                </section>
            </main>

            <section id="our-work" className="our-work-section">
                <div className="container">
                    <h2 className="section-heading section-heading--center">
                        <span>See our</span>
                        <span>weaving in action</span>
                    </h2>
                </div>
                <VideoCarousel
                    videos={[
                        'https://namma-nesavu-nool-backend-images-dev.s3.us-east-1.amazonaws.com/videos/1763127648418605.mp4',
                        'https://namma-nesavu-nool-backend-images-dev.s3.us-east-1.amazonaws.com/videos/1763127566610619.mp4',
                        'https://namma-nesavu-nool-backend-images-dev.s3.us-east-1.amazonaws.com/videos/1763127691895806.mp4',
                        'https://namma-nesavu-nool-backend-images-dev.s3.us-east-1.amazonaws.com/videos/1763128130817968.mp4',
                    ]}
                    autoplay={true}
                    loop={true}
                    interval={10000}
                    showControls={true}
                    showIndicators={true}
                />
            </section>

            <Footer onBulkOrderClick={handleBulkOrderClick} onEnquireClick={handleEnquireClick} />
            <WhatsAppFloat />

            {showContactForm && (
                <div className="modal-overlay" onClick={() => setShowContactForm(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowContactForm(false)}
                        >
                            ×
                        </button>
                        <ContactForm />
                    </div>
                </div>
            )}

            {showBulkOrderForm && (
                <div className="modal-overlay" onClick={() => setShowBulkOrderForm(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowBulkOrderForm(false)}
                        >
                            ×
                        </button>
                        <BulkOrderForm />
                    </div>
                </div>
            )}

            {showCart && (
                <Cart
                    onClose={() => {
                        setShowCart(false);
                        setShowCartCheckout(false);
                    }}
                    initialCheckout={showCartCheckout}
                />
            )}
        </div>
    );
};

export default UserView;
